import { approvalService } from '@/services/approval.service';
import type { RejectionPayload } from '@/services/rejection.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { purchaseRequisitionService } from '@/services/purchaseRequisition.service';
import { paymentVoucherService } from '@/services/paymentVoucher.service';
import { goodsReceiptNoteService } from '@/services/goodsReceiptNote.service';
import { supplierInvoiceService } from '@/services/supplierInvoice.service';
import salesOrderService from '@/services/salesOrder.service';
import {
  approveProductionRequest,
  rejectProductionRequest,
} from '@/services/productionRequest.service';
import quotationService from '@/services/quotation.service';
import salesInvoiceService from '@/services/salesInvoice.service';
import salesOrderTimesheetService from '@/services/salesOrderTimesheet.service';
import stockAdjustmentService from '@/services/stockAdjustment.service';
import { materialRequestService } from '@/services/materialRequest.service';
import { materialIssuanceService } from '@/services/materialIssuance.service';
import { documentCollectionService } from '@/services/document-collection.service';
import orderReviewChecklistService from '@/services/orderReviewChecklist.service';
import { purchaseReturnService } from '@/services/purchaseReturn.service';
import { crmEstimationService } from '@/services/crmEstimation.service';
import contractReviewService from '@/services/contractReview.service';
import intercompanyService from '@/services/intercompany.service';

export type DomainApproveResult = {
  fullyApproved: boolean;
  status?: string;
  currentStepNumber?: number;
  totalSteps?: number;
  nextLevelName?: string;
};

/**
 * Prefer document domain approve/reject APIs so multi-step engines and document
 * status stay in sync. Falls back to instance APIs for unmapped entity types
 * (e.g. MoC, inland deviation, EPC permit — ApprovalPanel via instance only).
 */
export async function approveViaDomainOrInstance(params: {
  entityType: string;
  entityId: string;
  instanceId: string;
  remarks?: string;
}): Promise<DomainApproveResult> {
  const { entityType, entityId, instanceId, remarks } = params;

  switch (entityType) {
    case 'purchase_order':
      await purchaseOrderService.approve(entityId, remarks);
      break;
    case 'purchase_requisition':
      await purchaseRequisitionService.approve(entityId, remarks);
      break;
    case 'payment_voucher':
      await paymentVoucherService.approveDocument(entityId, remarks);
      break;
    case 'goods_receipt_note':
      await goodsReceiptNoteService.approve(entityId, remarks);
      break;
    case 'supplier_invoice':
      await supplierInvoiceService.approve(entityId, remarks);
      break;
    case 'sales_order':
      await salesOrderService.approve(entityId, remarks);
      break;
    case 'production_request':
      await approveProductionRequest(entityId, remarks);
      break;
    case 'quotation':
      await quotationService.approve(entityId, remarks);
      break;
    case 'sales_invoice':
      await salesInvoiceService.approve(entityId, remarks);
      break;
    case 'stock_adjustment':
      await stockAdjustmentService.approve(entityId, remarks);
      break;
    case 'material_request':
      await materialRequestService.approve(entityId, remarks);
      break;
    case 'material_issuance':
      await materialIssuanceService.approve(entityId, remarks);
      break;
    case 'document_collection_request':
      await documentCollectionService.approve(entityId, remarks);
      break;
    case 'order_review_checklist':
      await orderReviewChecklistService.approve(entityId, remarks);
      break;
    case 'purchase_return':
      await purchaseReturnService.approve(entityId, remarks);
      break;
    case 'estimation':
      await crmEstimationService.approve(entityId, remarks);
      break;
    case 'contract_review':
      await contractReviewService.approve(entityId, remarks);
      break;
    case 'intercompany_transaction':
      await intercompanyService.approveTransaction(entityId, remarks);
      break;
    default:
      await approvalService.approveInstance(instanceId, remarks);
      break;
  }

  // Re-read instance for progress messaging (domain APIs may not return instance).
  try {
    const summary = await approvalService.getSummary(entityType, entityId);
    const inst = summary.instance;
    if (!inst) {
      return { fullyApproved: true };
    }
    const steps = inst.instanceSteps ?? [];
    const totalSteps = steps.length || undefined;
    const nextPending = steps.find((s) => s.status === 'pending');
    const nextLevelName = nextPending
      ? `Step ${nextPending.step_number}${nextPending.level_name ? ` (${nextPending.level_name})` : ''}`
      : undefined;
    return {
      fullyApproved: inst.status === 'approved',
      status: inst.status,
      currentStepNumber: inst.current_step_number,
      totalSteps,
      nextLevelName,
    };
  } catch {
    return { fullyApproved: true };
  }
}

export async function rejectViaDomainOrInstance(params: {
  entityType: string;
  entityId: string;
  instanceId: string;
  payload: RejectionPayload;
}): Promise<void> {
  const { entityType, entityId, instanceId, payload } = params;

  switch (entityType) {
    case 'purchase_order':
      await purchaseOrderService.rejectApproval(entityId, payload);
      return;
    case 'purchase_requisition':
      await purchaseRequisitionService.reject(entityId, payload);
      return;
    case 'payment_voucher':
      await paymentVoucherService.rejectApproval(entityId, payload);
      return;
    case 'goods_receipt_note':
      await goodsReceiptNoteService.rejectApproval(entityId, payload);
      return;
    case 'supplier_invoice':
      await supplierInvoiceService.rejectApproval(entityId, payload);
      return;
    case 'sales_order':
      await salesOrderService.rejectApproval(entityId, payload);
      return;
    case 'production_request':
      await rejectProductionRequest(entityId, payload);
      return;
    case 'quotation': {
      const reason = payload.rejection_reason_code || payload.remarks || 'Rejected';
      await quotationService.reject(entityId, reason);
      return;
    }
    case 'sales_invoice':
      await salesInvoiceService.rejectApproval(entityId, payload);
      return;
    case 'stock_adjustment':
      await stockAdjustmentService.rejectApproval(entityId, payload);
      return;
    case 'material_request':
      await materialRequestService.reject(entityId, payload);
      return;
    case 'material_issuance':
      await materialIssuanceService.rejectApproval(entityId, payload);
      return;
    case 'document_collection_request':
      await documentCollectionService.reject(entityId, payload as unknown as Record<string, unknown>);
      return;
    case 'order_review_checklist':
      await orderReviewChecklistService.rejectUniversal(entityId, {
        rejection_reason_code: payload.rejection_reason_code,
        remarks: payload.remarks,
      });
      return;
    case 'purchase_return':
      await purchaseReturnService.rejectApproval(entityId, payload);
      return;
    case 'estimation':
      await crmEstimationService.rejectUniversalWorkflow(entityId, payload);
      return;
    case 'contract_review':
      await contractReviewService.rejectUniversal(entityId, {
        rejection_reason_code: payload.rejection_reason_code,
        remarks: payload.remarks,
      });
      return;
    case 'intercompany_transaction':
      await intercompanyService.rejectTransaction(entityId, {
        rejection_reason_code: payload.rejection_reason_code,
        remarks: payload.remarks,
      });
      return;
    default:
      await approvalService.rejectInstance(instanceId, payload);
  }
}

export async function revokeViaDomainOrInstance(params: {
  entityType: string;
  entityId: string;
  instanceId: string;
  reason: string;
}): Promise<void> {
  const { entityType, entityId, instanceId, reason } = params;
  switch (entityType) {
    case 'purchase_order':
      await purchaseOrderService.revokeApproval(entityId, reason);
      return;
    case 'purchase_requisition':
      await purchaseRequisitionService.revokeApproval(entityId, reason);
      return;
    case 'payment_voucher':
      await paymentVoucherService.revokeApproval(entityId, reason);
      return;
    case 'goods_receipt_note':
      await goodsReceiptNoteService.revokeApproval(entityId, reason);
      return;
    case 'supplier_invoice':
      await supplierInvoiceService.revokeApproval(entityId, reason);
      return;
    case 'sales_order':
      await salesOrderService.revokeApproval(entityId, reason);
      return;
    case 'sales_order_timesheet':
      await salesOrderTimesheetService.revokeApproval(entityId, reason);
      return;
    case 'quotation':
      await quotationService.revokeApproval(entityId, reason);
      return;
    case 'sales_invoice':
      await salesInvoiceService.revokeApproval(entityId, reason);
      return;
    default:
      await approvalService.revokeInstance(instanceId, reason);
  }
}

export function formatApproveSuccessMessage(result: DomainApproveResult): string {
  if (result.fullyApproved) {
    return 'Fully approved';
  }
  if (result.nextLevelName) {
    return `Approved — next: ${result.nextLevelName}`;
  }
  if (result.currentStepNumber && result.totalSteps) {
    return `Approved — continuing (${result.currentStepNumber} of ${result.totalSteps})`;
  }
  return 'Approved — advanced to next step';
}
